import { inject } from '@angular/core';
import { AuthService, UserStore } from '@quipex/core/auth';
import {
    validateRuntimeEnvironment,
    type Environment,
} from '@quipex/core/config';
import { GENERAL_ERROR, injectError } from '@quipex/core/http';
import { DatadogService, MonitoringService } from '@quipex/core/logging';
import { ClientError } from '@quipex/core/utils';
import { firstValueFrom } from 'rxjs';

/**
 * Creates an app initializer function that validates Tier 2 (blocking) environment configuration.
 * Logs warnings and rejects bootstrap promise on blocking errors.
 */
export function validateEnvironmentAndInitialiseAppFn(
    environment: Environment
): () => Promise<void> {
    return async () => {
        const validationResult = validateRuntimeEnvironment(environment);

        // Only include error details in non-production and non-demo environments
        const isEnvNotProdOrDemo =
            environment.env === 'development' ||
            environment.env === 'qa' ||
            environment.env === 'uat';

        // Log warnings (warn-only keys)
        if (validationResult.warnings.length > 0) {
            const monitoring = inject(MonitoringService);
            // Monitoring self-starts and buffers while its lazy adapter loads.
            monitoring.logEvent('EnvironmentConfigurationWarning', {
                warnings: validationResult.warnings,
                timestamp: new Date().toISOString(),
            });
            const warningsList = validationResult.warnings
                .map((w) => '  - ' + w)
                .join('\n');
            const warningMessage =
                'Environment configuration warnings' +
                (isEnvNotProdOrDemo ? ':\n' + warningsList : '');
            console.warn(warningMessage);
        }

        if (
            validationResult.criticalErrors.length === 0 &&
            validationResult.blockingErrors.length === 0
        ) {
            const authService = inject(AuthService);
            const userStore = inject(UserStore);
            const datadogService =
                environment.env === 'development'
                    ? null
                    : inject(DatadogService);
            const isAuthenticated = await firstValueFrom(
                authService.initialize()
            );

            if (isAuthenticated) {
                const account = authService.activeAccount();
                if (account) {
                    userStore.ensureFreshForAuthenticatedAccount(account);
                } else {
                    userStore.fetch();
                }
            } else {
                userStore.clearAuthenticatedAccount();
            }

            void datadogService?.initialize();

            return Promise.resolve();
        }

        // Handle blocking errors - log via telemetry and reject bootstrap
        const clientError = injectError();
        const err = clientError.create(
            GENERAL_ERROR.EnvironmentConfigurationInvalid,
            {
                urnParts: ['unknown', 'environment', 'tier2-validation'],
                context: {
                    criticalErrors: validationResult.criticalErrors,
                    blockingErrors: validationResult.blockingErrors,
                },
                loggingContext: {
                    criticalErrors: validationResult.criticalErrors,
                    blockingErrors: validationResult.blockingErrors,
                    warningCount: validationResult.warnings.length,
                },
                ...(isEnvNotProdOrDemo ? {} : { consoleErrorRestArgs: [] }),
            }
        );

        // Do not include the context and logging context in the error
        const errWithoutContext = new ClientError(
            GENERAL_ERROR.EnvironmentConfigurationInvalid,
            { type: err.type, instance: err.instance }
        );
        return Promise.reject(errWithoutContext);
    };
}
